import { tWithKeyAttr as t } from "../i18nHelper";
import {
  IAppointmentProgram,
  IDocumentsToFilter,
  IMessage,
  ITherapist,
  ITreatmentPlan,
} from "../types/types";
import { Dispatch, SetStateAction } from "react";
import { DateObject, Value } from "react-multi-date-picker";
import {
  IChatAppointment,
  IQuestionAnswer,
  TGroupedUsersWithoutTherapistByFakeCompanyId,
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../services/types/user.types";
import { IUserStatus } from "../types/ws.types";
import { formatDateTime } from "./constants";

export function convertKeysToLowerCase(
  obj: Record<string, any>
): Record<string, any> {
  return Object.keys(obj).reduce((acc, key) => {
    const newKey = key.charAt(0).toLowerCase() + key.slice(1); // Convert the first letter to lowercase
    const value = obj[key];

    // Include only fields with a defined value (non-null, non-undefined, and not an empty string)
    if (value !== null && value !== undefined && value !== "") {
      acc[newKey] = value;
    }

    return acc;
  }, {} as Record<string, any>);
}

export function getDate(date: string, todayString: string, yesterdayString: string): string {
  const messageDate = new Date(date);
  const now = new Date();
  const messageYear = messageDate.getFullYear();
  const messageMonth = messageDate.getMonth();
  const messageDay = messageDate.getDate();
  const yearNow = now.getFullYear();
  const monthNow = now.getMonth();
  const dayNow = now.getDate();
  if (
    messageYear === yearNow &&
    messageMonth === monthNow &&
    messageDay === dayNow
  ) {
    return todayString;
  } else if (
    messageYear === yearNow &&
    messageMonth === monthNow &&
    dayNow - messageDay === 1
  ) {
    return yesterdayString;
  }
  return `${messageDay}/${messageMonth}/${messageYear}`;
}

// export const months: {
//   [key: number]: { value: string; titleKey: string };
// } = {
//   1: { value: "january", titleKey: "MONTH_JANUARY" },
//   2: { value: "february", titleKey: "MONTH_FEBRUARY" },
//   3: { value: "march", titleKey: "MONTH_MARCH" },
//   4: { value: "april", titleKey: "MONTH_APRIL" },
//   5: { value: "may", titleKey: "MONTH_MAY" },
//   6: { value: "june", titleKey: "MONTH_JUNE" },
//   7: { value: "july", titleKey: "MONTH_JULY" },
//   8: { value: "august", titleKey: "MONTH_AUGUST" },
//   9: { value: "september", titleKey: "MONTH_SEPTEMBER" },
//   10: { value: "october", titleKey: "MONTH_OCTOBER" },
//   11: { value: "november", titleKey: "MONTH_NOVEMBER" },
//   12: { value: "december", titleKey: "MONTH_DECEMBER" },
// };

const months = [
  "MONTH_JANUARY",
  "MONTH_FEBRUARY",
  "MONTH_MARCH",
  "MONTH_APRIL",
  "MONTH_MAY",
  "MONTH_JUNE",
  "MONTH_JULY",
  "MONTH_AUGUST",
  "MONTH_SEPTEMBER",
  "MONTH_OCTOBER",
  "MONTH_NOVEMBER",
  "MONTH_DECEMBER",
];

export const getMonth = (date: Date, t: (key: string) => string) => {
  const monthIndex = date.getMonth() + 1;
  return t(months[monthIndex]);
};

function formatNumberWithLeadingZero(num: number): string {
  return num < 10 ? `0${num}` : `${num}`;
}

export function getDuration(date: Date, minutesToAdd: number): string {
  const newDate = new Date(date);
  newDate.setMinutes(newDate.getMinutes() + minutesToAdd);
  return `${formatNumberWithLeadingZero(
    date.getHours()
  )}:${formatNumberWithLeadingZero(
    date.getMinutes()
  )}-${formatNumberWithLeadingZero(
    newDate.getHours()
  )}:${formatNumberWithLeadingZero(newDate.getMinutes())}`;
}
export const getTimeFromTimestamp = (date: Date | string) => {
  if (!date) return "";
  if (typeof date === "string") {
    date = new Date(date);
  }
  const timestamp = date.toTimeString().split(":");
  return `${timestamp[0]}:${timestamp[1]}`;
};

export function getPrintDate(date: Date, short?: boolean): string {
  return `${date.getDate()}/${date.getMonth() + 1}/${
    !short ? date.getFullYear() : date.getFullYear().toString().substr(-2)
  }`;
}

export function getPrintFormatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
}

export function formatDate(dateString: string): string {
  const date = dateString.includes("/")
    ? new Date(dateString.split("/").reverse().join("-"))
    : new Date(dateString);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

export function isExpired(date: Date): boolean {
  const now = new Date();
  return date < now;
}

export const passwordCheck = (
  value: string
): {
  strength: number;
  val: string;
} => {
  let pwdCheck = 0;
  let validateRegex = ["[A-Z]", "[a-z]", "[0-9]", "\\W"];

  value.length > 5 &&
    validateRegex.forEach((regex) => {
      if (new RegExp(regex).test(value)) {
        pwdCheck += 1;
      }
    });
  switch (pwdCheck) {
    case 2:
      return {
        strength: 2,
        val: t("DOCUMENT_URGENCY_MEDIUM"),
      };
    case 3:
      return {
        strength: 3,
        val: "חזק",
      };

    default:
      return {
        strength: 1,
        val: "חלש",
      };
  }
};

export function lowercaseKeys(obj: object): object {
  return Object.entries(obj).reduce((carry, [key, value]) => {
    // @ts-ignore
    carry[key.charAt(0).toLowerCase() + key.slice(1)] = value;
    return carry;
  }, {});
}

type ChangedValues = Partial<ITherapist>;

export const getChangedValues = (
  values: ITherapist,
  initialValues: ITherapist
): ChangedValues => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    const hasChanged = initialValues[key] !== value;

    if (hasChanged) {
      acc[key as keyof ITherapist] = value;
    }

    return acc;
  }, {} as ChangedValues);
};

export function isSameDateAndTime(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate() &&
    date1.getHours() === date2.getHours() &&
    date1.getMinutes() === date2.getMinutes()
  );
}

export const findById = (id: string, array: { id: string }[]) => {
  return array.find((d) => d.id === id);
};
// const filterDocumentsByDate = (dateValue) => {
//   if (dateValue) {
//     setDocumentsToShow(
//         filterByDate(therapistDocuments, dateValue as DateObject)
//     );
//   }
//   // setFiltersVisible(false);
// };
export const isSameDate = (date1: Date, date2: DateObject): boolean => {
  // Convert DateObject from react-calendar to a Date object
  const convertedDate = new Date(date2.year, date2.month.index, date2.day);

  // Check if the dates have the same year, month, and day
  return (
    date1.getFullYear() === convertedDate.getFullYear() &&
    date1.getMonth() === convertedDate.getMonth() &&
    date1.getDate() === convertedDate.getDate()
  );
};

export const filterByDate = <T extends IDocumentsToFilter>(
  array: T[],
  dateValue: Value
) => {
  if (dateValue instanceof DateObject) {
    return array.filter((doc) => isSameDate(doc.issued, dateValue));
  } else return array;
};

export const filterDocuments = <T extends IDocumentsToFilter>(
  filter: string,
  statusFilter: string | undefined,
  array: T[],
  setDocumentsToShow: Dispatch<SetStateAction<T[]>>
) => {
  const today = new Date();
  if (filter === "status" && statusFilter) {
    const filteredDocs = array.filter((doc) => doc.status === statusFilter);
    setDocumentsToShow(filteredDocs);
  } else if (filter === "urgent") {
    setDocumentsToShow(array.filter((d) => d.urgent));
  } else if (filter === "today") {
    setDocumentsToShow(
      array.filter((d) => getPrintDate(d.issued) === getPrintDate(today))
    );
  } else if (filter === "week") {
    if (today.getDay() === 0) {
      setDocumentsToShow(
        array.filter((d) => getPrintDate(d.issued) === getPrintDate(today))
      );
      return;
    }
    const startOfCurrentWeek = new Date(today);
    startOfCurrentWeek.setDate(today.getDate() - today.getDay());

    // Calculate the start of the last week (Sunday)
    const startOfLastWeek = new Date(startOfCurrentWeek);
    startOfLastWeek.setDate(startOfCurrentWeek.getDate() - 7);

    setDocumentsToShow(
      array.filter((d) => d.issued >= startOfLastWeek && d.issued <= today)
    );
  } else if (filter === "month") {
    const startOfCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );
    setDocumentsToShow(array.filter((d) => d.issued >= startOfCurrentMonth));
  } else {
    setDocumentsToShow(array);
  }
};

export function replaceVariables(text: string, userData: ITherapist): string {
  if (!text || !userData) return "";
  const userName = userData.firstName;
  const userGender = userData.genderId;
  // Replace @name with user name
  let replacedString = text.replace(/(@name|name@)/gi, userName);
  if (text.toLowerCase().includes("gender")) {
    replacedString = replacedString.replace(
      /(@gender_know|gender_know@)/gi,
      userGender === "נקבה" ? "יודעת" : "יודע"
    );
    replacedString = replacedString.replace(
      /(@gender_ready|gender_ready@)/gi,
      userGender === "נקבה" ? "מוכנה" : "מוכן"
    );
    replacedString = replacedString.replace(
      /(@gender|gender@)/gi,
      userGender === "נקבה" ? "את" : "אתה"
    );
  }

  return replacedString;
}
export function isTTherapistWithUsersArray(
  documents: any
): documents is TTherapistWithUsers[] {
  return (
    Array.isArray(documents) &&
    documents.every((item) => "therapistsId" in item)
  );
}
export function isToday(dateString: string): boolean {
  const today = new Date();
  const date = new Date(dateString);

  return (
    today.getDate() === date.getDate() &&
    today.getMonth() === date.getMonth() &&
    today.getFullYear() === date.getFullYear()
  );
}

export function isThisWeek(dateString: string): boolean {
  const today = new Date();
  const date = new Date(dateString);

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return date >= startOfWeek && date <= endOfWeek;
}

export function parseConnectionMessage(message: string): IUserStatus {
  // Regular expression to match the user ID and action (left or joined)
  const regex = /User (\d+) (left|joined) the room\./;
  const match = message.match(regex);

  if (match) {
    const userId = parseInt(match[1], 10);
    const connected = match[2] === "joined";

    return {
      userId,
      connected,
    };
  }

  // Return null or throw an error if the message format is incorrect
  throw new Error("Invalid message format");
}

export function parseQuestionAnswerString(
  jsonString: string
): Record<string, string> | undefined {
  // Parse the JSON string into an array of objects
  const questionAnswerArray: IQuestionAnswer[] = JSON.parse(jsonString);

  if (!questionAnswerArray) {
    return undefined;
  }
  // Initialize the result object
  const result: Record<string, string> = {};

  // Iterate through the array and build the result object
  questionAnswerArray.forEach((item) => {
    result[item.Question] = item.Answer.trim();
  });

  return result;
}
export function parseSpecificFieldsRaw(jsonString: string): IQuestionAnswer[] {
  try {
    const parsed = JSON.parse(jsonString);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function extractUniqueQuestionsAndAnswers(
  documents: { specificFields: string }[]
): Map<string, string[]> {
  const map = new Map<string, Set<string>>();
  for (const doc of documents) {
    for (const qa of parseSpecificFieldsRaw(doc.specificFields)) {
      const q = qa.Question?.trim();
      const a = qa.Answer?.trim();
      if (!q || !a) continue;
      if (!map.has(q)) map.set(q, new Set());
      map.get(q)!.add(a);
    }
  }
  const result = new Map<string, string[]>();
  for (const [q, answers] of map) {
    result.set(q, Array.from(answers));
  }
  return result;
}

export function matchesSpecificQuestionFilters(
  specificFieldsJson: string,
  filters: { question: string; answers: string[] }[]
): boolean {
  const activeFilters = filters.filter((f) => f.answers.length > 0);
  if (activeFilters.length === 0) return true;
  const entries = parseSpecificFieldsRaw(specificFieldsJson);
  // AND across questions, OR within same question
  return activeFilters.every((filter) =>
    entries.some((e) => {
      const q = e.Question?.trim();
      const a = e.Answer?.trim();
      if (!q || !a) return false;
      return q === filter.question && filter.answers.includes(a);
    })
  );
}

// Function to filter the appointments and remove duplicates based on the highest ID
export const filterAppointments = (
  appointments: IChatAppointment[]
): IChatAppointment[] => {
  // Map to keep track of the highest ID appointment for each user
  const uniqueAppointmentsMap = new Map<number, IChatAppointment>();
  // console.log(appointments);
  appointments.forEach((appointment) => {
    const existingAppointment = uniqueAppointmentsMap.get(appointment.usersId);
    if (!existingAppointment || appointment.id > existingAppointment.id) {
      uniqueAppointmentsMap.set(appointment.usersId, appointment);
    }
  });

  // Convert the map values to an array of IChatAppointment
  return Array.from(uniqueAppointmentsMap.values());
};

// export function getAppointmentsMessages(
//   appointments: IChatAppointment[]
// ): { userId: number; messages: { date: string; messages: IMessage[] }[] }[] {
//   const groupedAppointments: { [key: string]: IMessage[] } = {};
//
//   appointments.forEach((appointment) => {
//     const storedMessagesString = sessionStorage.getItem(String(appointment.Id));
//
//     let messages: IMessage[];
//     try {
//       messages = appointment.AppointmentReport
//         ? JSON.parse(appointment.AppointmentReport)
//         : [];
//       if (!Array.isArray(messages)) {
//         messages = [];
//       }
//       //add messages from the sessionStorage
//       const storedMessages = storedMessagesString
//         ? JSON.parse(storedMessagesString)
//         : [];
//       messages = [...messages, ...storedMessages];
//     } catch (error) {
//       console.error(
//         `Error parsing comments for appointment ID ${appointment.Id}:`,
//         error
//       );
//       messages = [];
//     }
//
//     if (!groupedAppointments[appointment.UsersId]) {
//       groupedAppointments[appointment.UsersId] = [];
//     }
//     groupedAppointments[appointment.UsersId].push(...messages);
//   });
//
//   return Object.keys(groupedAppointments).map((key: string) => ({
//     userId: Number(key),
//     messages:
//       groupedAppointments[Number(key)].length > 0
//         ? sortMessagesByDate(groupedAppointments[Number(key)])
//         : [],
//   }));
// }

export function sortMessagesByDate(
  messages: IMessage[]
): { date: string; messages: IMessage[] }[] {
  // sort messages by date
  // Temporary storage for grouped messages
  const groupedByDate: { [key: string]: IMessage[] } = {};

  // Group messages by date (ignoring time)
  messages.forEach((m) => {
    const date = formatDateTime(new Date(m.date)).split("T")[0]; // Extract the date part (YYYY-MM-DD)
    if (!groupedByDate[date]) {
      groupedByDate[date] = [];
    }
    groupedByDate[date].push(m);
  });

  // Convert grouped messages to the desired format and sort by date
  const groupedMessages = Object.keys(groupedByDate).map((date) => ({
    date,
    messages: groupedByDate[date],
  }));

  // Sort the grouped messages by date
  groupedMessages.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  return groupedMessages;
}

export function mergeUsersWithAppointments(
  users: {
    userId: number;
    messages: { date: string; messages: IMessage[] }[];
  }[],
  appointments: IChatAppointment[]
): {
  userId: number;
  messages: { date: string; messages: IMessage[] }[];
  appointment?: IChatAppointment;
}[] {
  // Create a map to store appointments by userId
  if (appointments.length === 0) {
    return users;
  }
  const appointmentsMap: { [key: number]: IChatAppointment | undefined } = {};

  // Populate the map with the first appointment found for each userId
  appointments.forEach((appointment) => {
    if (!appointmentsMap[appointment.usersId]) {
      appointmentsMap[appointment.usersId] = appointment;
    }
  });

  // Merge users with their corresponding appointment
  return users.map((user) => ({
    ...user,
    appointment: appointmentsMap[user.userId],
  }));
}
export const toggleMuteMic = (
  mediaStream: MediaStream | undefined,
  setIsMicMuted: (a: boolean) => void
) => {
  if (mediaStream) {
    const audioTrack = mediaStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsMicMuted(!audioTrack.enabled); // Update state to reflect mute/unmute
    }
  }
};

export const toggleVideo = (
  mediaStream: MediaStream | undefined,
  setIsVideoEnabled: Dispatch<SetStateAction<boolean>>
) => {
  if (mediaStream) {
    const videoTracks = mediaStream.getVideoTracks();
    if (videoTracks.length > 0) {
      // Toggle track.enabled (do not stop the track)
      const isEnabled = videoTracks[0].enabled;
      videoTracks.forEach((track) => {
        if (track.readyState === "live") {
          track.enabled = !track.enabled;
        }
      });
      setIsVideoEnabled(!isEnabled);
    }
  }
};

export const isDateAfterToday = (dateStr: Date | string): boolean => {
  const inputDate = new Date(dateStr).setHours(0, 0, 0, 0); // Remove time component
  const today = new Date().setHours(0, 0, 0, 0); // Today's date without time
  return inputDate > today; // True if the date is after today
};

export const groupByFakeCompanyId = (
  items: TUsersWithOutTherapist[]
): TGroupedUsersWithoutTherapistByFakeCompanyId => {
  return items.reduce<TGroupedUsersWithoutTherapistByFakeCompanyId>(
    (acc, item) => {
      const key = item.fakeCompanyId ?? "null"; // Use 'null' as a string for null fakeCompanyId
      if (!acc[key]) {
        acc[key] = [];
      } else if (
        acc[key].length > 0 &&
        acc[key][0].fakeCompanyName !== item.fakeCompanyName
      ) {
        // console.warn(`Inconsistent FakeCompanyName for FakeCompanyId ${key}:`, {
        //   existing: acc[key][0]?.FakeCompanyName ?? "",
        //   new: item.FakeCompanyName ?? "",
        // });
      }
      acc[key]?.push(item);
      return acc;
    },
    { null: [] } // Initial accumulator with the 'null' key
  );
};

export const capitalizeKeys = (
  obj: Record<string, any>
): Record<string, any> => {
  if (!obj || typeof obj !== "object") {
    throw new Error("Input must be an object");
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
    acc[capitalizedKey] = value;
    return acc;
  }, {} as Record<string, any>);
};

export const checkProgramRestrictions = (
  appointmentProgram: IAppointmentProgram,
  therapist: ITherapist
): string => {
  if (
    appointmentProgram.vetek &&
    appointmentProgram.vetek > (therapist.vetek ?? 0)
  )
    return "PROGRAM_RESTRICTION_VETEK_MISMATCH";
  if (
    appointmentProgram.specializationsId &&
    appointmentProgram.specializationsId !== (therapist.SpecializationsId ?? 0)
  )
    return "PROGRAM_RESTRICTION_SPECIALIZATION_MISMATCH";
  if (
    appointmentProgram.roleId &&
    appointmentProgram.roleId !== therapist.roleId
  )
    return "PROGRAM_RESTRICTION_ROLE_MISMATCH";
  if (
    appointmentProgram.genderId &&
    appointmentProgram.genderId !== therapist.GenderId
  )
    return "PROGRAM_RESTRICTION_GENDER_MISMATCH";
  if (
    appointmentProgram.migzarId &&
    appointmentProgram.migzarId !== therapist.MigzarId
  )
    return "PROGRAM_RESTRICTION_MIGZAR_MISMATCH";
  return "PROGRAM_RESTRICTION_FAILED_DEFAULT";
};

export const getMimeType = (fileUrl: string): string => {
  const extension = fileUrl.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "pdf":
      return "application/pdf";
    default:
      return "application/octet-stream";
  }
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const allowedExternalVideoHosts = new Set([
  "www.youtube.com",
  "youtube.com",
  "youtu.be",
  "www.vimeo.com",
  "vimeo.com",
]);

const isYouTubeVideoId = (value?: string | null): value is string =>
  !!value && /^[A-Za-z0-9_-]{11}$/.test(value);

const isVimeoVideoId = (value?: string | null): value is string =>
  !!value && /^\d+$/.test(value);

const getYouTubeVideoId = (uri: URL): string | null => {
  const pathParts = uri.pathname.split("/").filter(Boolean);

  if (uri.hostname.toLowerCase() === "youtu.be") {
    const id = pathParts[0];
    return isYouTubeVideoId(id) ? id : null;
  }

  const pathVideoId =
    ["embed", "v", "shorts"].includes(pathParts[0]) ? pathParts[1] : null;
  if (isYouTubeVideoId(pathVideoId)) return pathVideoId;

  const queryVideoId = uri.searchParams.get("v");
  return isYouTubeVideoId(queryVideoId) ? queryVideoId : null;
};

const getVimeoVideoId = (uri: URL): string | null => {
  const id = uri.pathname.split("/").filter(Boolean).find(isVimeoVideoId);
  return id ?? null;
};

export const getAllowedExternalVideoEmbedUrl = (url: string): string | null => {
  try {
    const uri = new URL(url.trim());
    const host = uri.hostname.toLowerCase();

    if (uri.protocol !== "https:" || !allowedExternalVideoHosts.has(host)) {
      return null;
    }

    if (host === "youtu.be" || host === "youtube.com" || host === "www.youtube.com") {
      const videoId = getYouTubeVideoId(uri);
      return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : null;
    }

    const videoId = getVimeoVideoId(uri);
    return videoId ? `https://player.vimeo.com/video/${videoId}?dnt=1` : null;
  } catch {
    return null;
  }
};

export const isAllowedExternalVideoUrl = (url: string): boolean =>
  getAllowedExternalVideoEmbedUrl(url) !== null;



export const formatIcdCode = (code: string): string => {
  if (!code || code.length <= 1) return code;
  return code.slice(0, -1) + '.' + code.slice(-1);
};

export const extractCodeFromLabel = (label: string): string => {
  if (!label) return label;
  const m = /\(([^)]+)\)\s*$/.exec(label);
  if (m) return m[1].replace('.', '');
  return label.replace('.', '');
};

// Find a server option that exactly matches a suggested ICD code.
// Returns a TLabeledQuestionOption-like object or null if not found.
export const findIcdOptionBySuggestedCode = (questionOptions: any[] = [], suggestedCode: string, min?: number) => {
  if (!suggestedCode) return null;
  const normalizedSug = (suggestedCode || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

  for (const opt of questionOptions) {
    const label = (opt.Desc || "").toString();
    const parenMatch = label.match(/\(([^)]+)\)\s*$/);
    if (parenMatch && parenMatch[1]) {
      const normalizedOptCode = parenMatch[1].replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      if (normalizedOptCode === normalizedSug) {
        return {
          ...opt,
          count: opt.count,
          min: min ?? null,
          value: opt.Id?.toString?.() ?? "",
          label: opt.Desc,
        } as any;
      }
    }
  }

  return null;
};

// Convert an option code to dotted format using the option's group ancestry.
// Example: optionCode='F50002', group chain ['F500','F5000'] -> choose longest proper prefix and return 'F500.02'
export const toDottedIcdCode = (optionCode: string, serverOption: any): string => {
  if (!optionCode) return optionCode;
  const normalizedOption = (optionCode || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();

  // collect group chain codes
  let group = serverOption?.Groups ?? null;
  const candidates: string[] = [];
  while (group) {
    const name = group.Name ?? group.name ?? "";
    const extracted = extractCodeFromLabel(name?.toString?.() ?? "") || (name.toString().match(/^([A-Za-z0-9]+)/)?.[1] ?? null);
    if (extracted) candidates.push(extracted.toUpperCase());
    group = group.ParentGroup;
  }

  // pick longest candidate that is a proper prefix
  let chosen: string | null = null;
  for (const c of candidates) {
    if (c && normalizedOption.startsWith(c) && c.length < normalizedOption.length) {
      if (!chosen || c.length > chosen.length) chosen = c;
    }
  }

  if (chosen) {
    const rest = normalizedOption.slice(chosen.length);
    return `${chosen}.${rest}`;
  }

  return optionCode;
};
export function filterUniquePrograms(
  programs: Partial<IAppointmentProgram>[]
): Partial<IAppointmentProgram>[] {
  const uniqueNames = new Set<string | null>();

  return programs.filter((program) => {
    if (program.name && !uniqueNames.has(program.name)) {
      uniqueNames.add(program.name);
      return true;
    }
    return false;
  });
}

export const getCategory = (
  categories: { id: number; name: string }[],
  id: number,
): string => {
  const category = categories.find((c) => c.id === id);
  const defaultLabel = "GENERAL";
  return category ? category.name : t(defaultLabel);
};

export function parseSummaryText(summary?: string): string {
  if (!summary) {
    return "";
  }

  let text = summary;

  // Extract inner content from markdown code fences if present
  const fencedMatch = text.match(/```(?:json|js|txt)?\s*([\s\S]*?)\s*```/i);
  if (fencedMatch) {
    const inner = fencedMatch[1].trim();
    try {
      const parsed = JSON.parse(inner);
      if (parsed && typeof (parsed as any).summary === "string") {
        return (parsed as any).summary.trim();
      }
      if (typeof parsed === "string") {
        return parsed.trim();
      }
    } catch {}
    text = inner;
  }

  // Try to parse as JSON directly
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof (parsed as any).summary === "string") {
      return (parsed as any).summary.trim();
    }
    if (typeof parsed === "string") {
      return parsed.trim();
    }
  } catch {}

  // Strip any remaining code fences and HTML tags
  text = text.replace(/```/g, "");
  text = text.replace(/<[^>]*>/g, "");

  return text.trim();
}
export function getUpdateReasonLabel(updateReason: string | null, questionnaireId?: number): string {
  // Handle null updateReason - if questionnaireId exists, treat as initial questionnaire
  if (!updateReason) {
    if (questionnaireId) {
      return `${t("HISTORY_REASON_INITIAL")} ${questionnaireId}`;
    }
    return '';
  }

  switch (updateReason) {
    case "1":
    case "InitialQuestionnaire":
      return questionnaireId 
        ? `${t("HISTORY_REASON_INITIAL")} ${questionnaireId}`
        : t("HISTORY_REASON_INITIAL");
    case "2":
    case "NewQuestionnaire":
      return questionnaireId 
        ? `${t("HISTORY_REASON_NEW_QUESTIONNAIRE")} ${questionnaireId}`
        : t("HISTORY_REASON_NEW_QUESTIONNAIRE");
    case "3":
    case "TherapistResponse":
      return t("HISTORY_REASON_THERAPIST_UPDATED");
    default:
      return updateReason;
  }
}

export function getUpdateReasonColor(updateReason: string | null, styles: Record<string, string>): string {
  // Handle null updateReason - default to initial badge color
  if (!updateReason) {
    return styles.historyBadge_initial;
  }

  switch (updateReason) {
    case "1":
    case "InitialQuestionnaire":
      return styles.historyBadge_initial;
    case "2":
    case "NewQuestionnaire":
      return styles.historyBadge_questionnaire;
    case "3":
    case "TherapistResponse":
      return styles.historyBadge_therapist;
    default:
      return styles.historyBadge_initial;
  }
}
export   const timeToMinutes = (time: string) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};
